import { MutationCache, QueryClient, type Query } from '@tanstack/react-query';

type QueryTimingOptions = {
  gcTime?: number;
  staleTime?: number | 'static' | ((query: Query) => number | 'static' | undefined);
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const getTimingOptions = (options: unknown): QueryTimingOptions => options as QueryTimingOptions;

const resolveStaleTime = (staleTime: QueryTimingOptions['staleTime'], query: Query): number | undefined => {
  if (typeof staleTime === 'function') {
    return resolveStaleTime(staleTime(query), query);
  }

  if (staleTime === 'static') {
    return Number.POSITIVE_INFINITY;
  }

  return staleTime;
};

const getEffectiveStaleTime = (queryClient: QueryClient, query: Query) => {
  const defaultStaleTime =
    resolveStaleTime(getTimingOptions(queryClient.getQueryDefaults(query.queryKey)).staleTime, query) ?? 0;
  const observerStaleTimes = query.observers
    .map((observer) => resolveStaleTime(observer.options.staleTime, query))
    .filter(isDefined);

  if (query.getObserversCount() > 0 && observerStaleTimes.length > 0) {
    return Math.min(...observerStaleTimes);
  }

  return resolveStaleTime(getTimingOptions(query.options).staleTime, query) ?? defaultStaleTime;
};

const getEffectiveGcTime = (queryClient: QueryClient, query: Query) => {
  const defaultGcTime = getTimingOptions(queryClient.getQueryDefaults(query.queryKey)).gcTime ?? 0;
  const observerGcTimes = query.observers.map((observer) => observer.options.gcTime).filter(isDefined);

  if (query.getObserversCount() > 0 && observerGcTimes.length > 0) {
    return Math.min(...observerGcTimes);
  }

  return getTimingOptions(query.options).gcTime ?? defaultGcTime;
};

const shouldInvalidateQuery = (queryClient: QueryClient, query: Query) => {
  const staleTime = getEffectiveStaleTime(queryClient, query);
  const gcTime = getEffectiveGcTime(queryClient, query);

  return staleTime !== Number.POSITIVE_INFINITY || gcTime !== Number.POSITIVE_INFINITY;
};

export const createDefaultQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 2,
        throwOnError: true,
      },
      mutations: {
        retry: 2,
        throwOnError: true,
      },
    },
    mutationCache: new MutationCache({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) => shouldInvalidateQuery(queryClient, query),
        });
      },
    }),
  });

  return queryClient;
};
